<template>
  <v-progress-linear v-if="loading" indeterminate />
  <v-container v-else :class="{ sticky: sticky }">
    <v-row class="align-center justify-center">
      <v-slide-group
        :model-value="selectedImages"
        @update:model-value="updateSelected"
        class="pa-2"
        show-arrows
        center-active
        multiple
      >
        <v-slide-group-item
          v-for="(image, index) in imageLinks"
          :key="index"
          :value="index"
          v-slot="{ isSelected, toggle }"
        >
          <v-card
            :color="isSelected ? 'yellow' : 'grey-lighten-3'"
            elevation="0"
            class="ma-2"
            height="70"
            width="70"
            @click="toggle"
          >
            <v-card-text class="justify-center pa-2">
              <v-img
                :src="image.link"
                :aspect-ratio="1 / 1"
                class="rounded"
                @error="$emit('image-error', image.link)"
              >
                <v-row class="pt-5 align-center justify-center">
                  <v-scale-transition>
                    <v-icon
                      v-if="isSelected"
                      color="white"
                      size="35"
                      icon="mdi-close-circle-outline"
                    ></v-icon>
                  </v-scale-transition>
                </v-row>
              </v-img>
            </v-card-text>
          </v-card>
        </v-slide-group-item>
      </v-slide-group>
    </v-row>
    <v-row class="align-start">
      <v-col
        v-for="(selection, idx) in selectedImages"
        :key="idx"
        :cols="selectedImages.length > 1 ? 12 / selectedImages.length : 12"
      >
        <v-row v-if="imageLinks.length > 0">
          <v-col>
            <v-row class="justify-center align-center">
              <div class="text-center">
                <span
                  ><b>Side: </b>{{ parseSide(imageLinks[selection].side) }}
                  <br />
                  <b>View: </b>{{ parseView(imageLinks[selection].view) }}</span
                >
              </div>
              <div class="mr-n6">
                <v-btn
                  v-if="compressedImages.includes(selection)"
                  icon
                  variant="text"
                  height="40px"
                  width="40px"
                  :key="`expand-${selection}`"
                  @click="expandImage(selection)"
                  location="right"
                  ><v-icon>mdi-arrow-expand-horizontal</v-icon>
                </v-btn>
                <v-btn
                  v-else
                  icon
                  variant="text"
                  height="40px"
                  width="40px"
                  :key="`compress-${selection}`"
                  @click="compressImage(selection)"
                  location="right"
                  ><v-icon>mdi-arrow-collapse-horizontal</v-icon>
                </v-btn>
              </div>
            </v-row>
          </v-col>
        </v-row>
        <v-row class="justify-center">
          <v-card
            v-if="!compressedImages.includes(selection)"
            class="overflow-auto"
            max-height="55vh"
            variant="outlined"
          >
            <v-row class="justify-center">
              <inner-image-zoom
                :src="imageLinks[selection].link"
                moveType="drag"
              />
            </v-row>
          </v-card>
          <v-card
            v-else
            :image="imageLinks[selection].link"
            :width="getWidth(imageLinks[selection].link)"
            height="55vh"
            variant="outlined"
          >
          </v-card>
        </v-row>
        <v-row class="justify-center">
          <span class="text-center">
            Photo Source:
            {{ imageLinks[selection].label || 'Unavailable' }}
            <br />
            For more information in photo, click (here).</span
          >
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>

<script lang="ts">
import { defineComponent, PropType, Ref, ref, watch } from 'vue';
import { EpigraphyLabelLink } from '@oare/types';
import InnerImageZoom from 'vue-inner-image-zoom';

export default defineComponent({
  components: {
    InnerImageZoom,
  },
  props: {
    imageLinks: {
      type: Array as PropType<EpigraphyLabelLink[]>,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    maxSelect: {
      type: Number,
      default: 2,
    },
    sticky: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['image-error'],
  setup(props) {
    const selectedImages = ref([0]);
    const compressedImages: Ref<number[]> = ref([]);

    // The parent removes links that fail to load, which shifts the indices.
    watch(
      () => props.imageLinks.length,
      length => {
        selectedImages.value = selectedImages.value.filter(i => i < length);
        if (selectedImages.value.length === 0 && length > 0) {
          selectedImages.value = [0];
        }
        compressedImages.value = [];
      }
    );

    const compressImage = (selection: number) => {
      compressedImages.value.push(selection);
    };

    const expandImage = (selection: number) => {
      const indexToRemove = compressedImages.value.indexOf(selection);
      compressedImages.value.splice(indexToRemove, 1);
    };

    const getWidth = (src: string) => {
      const image = new Image();
      image.src = src;
      const heightRatio = (window.innerHeight * 0.55) / image.height;

      return (heightRatio * image.width + 1).toFixed(0);
    };

    const updateSelected = (selection: number[]) => {
      selectedImages.value = selection;
      compressedImages.value = [];
      if (selectedImages.value.length > props.maxSelect) {
        selectedImages.value.shift();
      }
    };

    const parseSide = (sideCode: string | number | null) => {
      switch (sideCode) {
        case 1:
          return 'obv.';
        case 2:
          return 'lo.e.';
        case 3:
          return 'rev.';
        case 4:
          return 'u.e.';
        case 5:
          return 'le.e.';
        case 6:
          return 'r.e.';
        case 'a':
          return 'Envelope Inner obv.';
        case 'b':
          return 'Envelope Inner lo.e.';
        case 'c':
          return 'Envelope Inner rev.';
        case 'd':
          return 'Envelope Inner u.e.';
        case 'e':
          return 'Envelope Inner le.e.';
        case 'f':
          return 'Envelope Inner r.e.';
        case 'x':
          return 'Fat Cross';
        case 'h':
          return 'Handcopy';
        case 8:
          return 'Unknown Side';
        case 9:
          return 'Unknown Edge';
        case null:
          return 'No side information';
        default:
          return sideCode;
      }
    };

    const parseView = (viewCode: string | null) => {
      switch (viewCode) {
        case 'u':
          return 'Upper (from above)';
        case 'd':
          return 'Direct';
        case 'l':
          return 'Lower';
        case 'i':
          return 'Inverted Direct';
        case 'h':
          return 'Inverted Upper';
        case 'j':
          return 'Inverted Lower';
        case 'z':
          return 'View for Scale';
        case 'n':
          return 'Emphasis on Name';
        case 's':
          return 'Seal Impression Focus';
        case 'fr':
          return 'From Right';
        case 'fl':
          return 'From Left';
        case 'cx':
          return 'Contextual';
        case 'du':
          return 'Detail Unspecified';
        case null:
          return 'No view information';
        default:
          return viewCode;
      }
    };

    return {
      selectedImages,
      compressedImages,
      updateSelected,
      parseSide,
      parseView,
      compressImage,
      expandImage,
      getWidth,
    };
  },
});
</script>

<style scoped>
.sticky {
  position: sticky;
  top: 0.9in;
}
</style>
